<template>
  <IconButton v-bind="props" @click="onClick" :class="{ 'is-loading': loading }">
    <template #icon>
      <slot name="icon"></slot>
    </template>
    <slot></slot>
    <div v-if="loading" class="loading-overlay">
      <Loader :type="loaderType" :theme-color="computedLoaderColor" :size="loaderSize" />
    </div>
  </IconButton>
</template>

<script setup lang="ts">
import IconButton from "./IconButton.vue";
import { Loader } from "@progress/kendo-vue-indicators";
import { computed } from "vue";

export interface LoadingIconButtonProps {
  label?: string;
  icon?: string;
  iconBaseClass?: string;
  type?: "button" | "submit" | "reset";
  iconSize?: string;
  loading: boolean;
  disableOnLoading?: boolean;
  loaderColor?: string;
  loaderType?: string;
  loaderSize?: string;
}

const defaultLoaderColor = "primary";

const props = withDefaults(defineProps<LoadingIconButtonProps>(), {
  label: "",
  icon: "",
  iconBaseClass: "fa-solid",
  type: "button",
  iconSize: "xl",
  loaderType: "pulsing",
  loaderSize: "medium",
  loaderColor: defaultLoaderColor,
  disableOnLoading: true,
});

const computedLoaderColor = computed(() => {
  return props.loaderColor || defaultLoaderColor;
});

const emit = defineEmits<{
  (e: "click"): void;
}>();

const onClick = () => {
  if (!props.disableOnLoading || !props.loading) {
    emit("click");
  }
};
</script>

<style scoped lang="scss">
.is-loading {
  position: relative;
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
}
</style>
